import styled from '@emotion/styled';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { FC, useEffect, useRef } from 'react';
import {
  FILTER_TYPE,
  SearchFilterType,
  change_price_atom,
  remove_category_atom,
  remove_color_atom,
  reset_client_search_options_atom,
  client_search_options_atom,
  set_free_shipping_atom,
} from '../../../../store/searchSheet';
import { Button } from 'components/base/Button';
import Chip from 'components/base/Chip';
import { IconCancel16, IconRefresh16 } from 'components/base/Icon';
import Text from 'components/base/Text';
import { invisible_scroll_bar } from 'components/products/details/styles';
import useSearchFilter from 'components/search/hooks/useSearchFilter';
import { usePreviousValue } from 'hooks';
import { WEB_MAX_WIDTH, border4, gray150, gray800, gray_active, gray_disabled, white } from 'styles';
import LoadingManager from 'util/loading_manager';
import { numberwithComma } from 'util/number';
import { getPriceKo } from 'util/search_utils';
import { Log, useTracker } from 'util/Tracker';

interface Props {
  reset_ubl: Log;
  onSearch: () => void;
}

/**
 * 검색 필터 - 하단 검색
 * @component
 */
const Footer: FC<Props> = ({ onSearch, reset_ubl }) => {
  const chip_ref = useRef<HTMLUListElement | null>(null);
  const client_search_options = useAtomValue(client_search_options_atom);
  const prev_client_search_options = usePreviousValue<SearchFilterType[]>(client_search_options);
  const tracker = useTracker();
  const { total_count, is_loading } = useSearchFilter();
  const removeCategory = useUpdateAtom(remove_category_atom);
  const removeColor = useUpdateAtom(remove_color_atom);
  const setFreeShipping = useUpdateAtom(set_free_shipping_atom);
  const changePrice = useUpdateAtom(change_price_atom);
  const resetFilter = useUpdateAtom(reset_client_search_options_atom);

  useEffect(() => {
    is_loading ? LoadingManager.open() : LoadingManager.close(0);
    return () => LoadingManager.close(0);
  }, [is_loading]);

  // MEMO: 검색 필터 조건이 추가되는경우 해당 추가된 chip으로 scroll 이동 시켜서 인지시켜주기위한 UX개선용도
  useEffect(() => {
    const is_add_chip = prev_client_search_options && client_search_options.length > prev_client_search_options.length;
    if (is_add_chip) {
      const max_left_scroll = chip_ref.current ? chip_ref.current.scrollWidth - chip_ref.current.clientWidth : 0;
      chip_ref.current?.scrollBy({ left: max_left_scroll });
    }
  }, [client_search_options, prev_client_search_options]);

  const handleResetClick = () => {
    if (!is_active_reset) {
      return;
    }

    tracker.addLog(reset_ubl);
    resetFilter();
  };

  const handleRemoveSearchChipClick = (search_filter: SearchFilterType) => {
    switch (search_filter.type) {
      case FILTER_TYPE.CATEGORY: {
        removeCategory(search_filter.value.id);
        return;
      }
      case FILTER_TYPE.COLOR: {
        removeColor(search_filter.value);
        return;
      }
      case FILTER_TYPE.PRICE: {
        changePrice({ min: search_filter.value.min, max: search_filter.value.max });
        return;
      }
      case FILTER_TYPE.DELIVERY: {
        if (search_filter.value.is_free_shipping) {
          setFreeShipping(false);
        }
        return;
      }
    }
  };

  const search_chip_list = client_search_options.map((item) => {
    const name = getSearchChipName(item);
    return { value: item, name };
  });

  const display_total_count = numberwithComma(total_count, { max_count: 9_999, max_count_subfix: '+' });
  const is_active_reset = client_search_options.length > 0;
  const is_active_search = total_count > 0;

  return (
    <SC.Container>
      {search_chip_list.length > 0 && (
        <SC.Chips ref={chip_ref}>
          {search_chip_list.map(({ name, value }, index) => (
            <li key={`${name}-${value.type}-${index}`}>
              <Chip
                active
                role='section'
                varient='filled'
                size='small'
                className='search_chip'
                text={name}
                onClick={() => handleRemoveSearchChipClick(value)}
                end_icon={<IconCancel16 />}
              />
            </li>
          ))}
        </SC.Chips>
      )}
      <SC.Search>
        <button className='reset' onClick={handleResetClick}>
          <IconRefresh16 color={is_active_reset ? gray_active : gray_disabled} />
          <Text variant='title4_medium' color={is_active_reset ? 'gray_active' : 'gray_disabled'} mr={8}>
            초기화
          </Text>
        </button>
        <Button
          fill
          size='large'
          variant='primary'
          className='search-button'
          disabled={!is_active_search}
          loading={is_loading}
          onClick={onSearch}
        >
          {is_active_search ? `${display_total_count}개 상품 보기` : '검색 가능한 상품이 없습니다.'}
        </Button>
      </SC.Search>
    </SC.Container>
  );
};

export default Footer;

function getSearchChipName(item: SearchFilterType) {
  switch (item.type) {
    case FILTER_TYPE.PRICE:
      return getPriceKo(item.value.min, item.value.max);
    case FILTER_TYPE.CATEGORY:
      return item.value.name;
    case FILTER_TYPE.DELIVERY:
      return getDeliveryKo({ is_free_shipping: item.value.is_free_shipping });
    default:
      return item.value;
  }
}

function getDeliveryKo({ is_free_shipping }: { is_free_shipping: boolean }) {
  if (is_free_shipping) {
    return '무료배송';
  }
  return '';
}

const SC = {
  Container: styled.footer`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: ${WEB_MAX_WIDTH}px;
    width: 100%;
    margin: 0 auto;
    z-index: 1;
    background-color: ${white};
  `,
  Chips: styled.ul`
    ${invisible_scroll_bar}
    padding: 8px 0;
    white-space: nowrap;
    overflow-x: scroll;
    overflow-y: hidden;
    position: relative;
    gap: 6px;
    height: 48px;
    display: flex;
    align-items: center;
    scrollbar-width: none;
    background-color: ${gray150};

    & li:first-of-type {
      margin-left: 16px;
    }

    & li:last-of-type {
      margin-right: 16px;
    }

    .search_chip {
      background-color: ${gray800};
      border-color: ${gray800};
    }
  `,
  Search: styled.div`
    display: flex;
    padding: 8.5px 16px;
    align-items: center;
    border-top: 1px solid ${border4};

    .search-button {
      flex: 1;
    }

    .reset {
      width: 100px;
      height: 51px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      > svg {
        margin-right: 4px;
      }
    }
  `,
};
